export const FUEL_LAUNCHES = 5;
export const FUEL_MIN = 1;
export const FUEL_MAX = 10;
export const FUEL_MAX_STARS = 13;

export interface FuelBand {
  min: number;
  max: number;
}

export const FUEL_BANDS: ReadonlyArray<FuelBand> = [
  { min: 1, max: 3 },
  { min: 4, max: 6 },
  { min: 7, max: 10 },
];

/** Maps a 0-based launch index to a difficulty band (0,1 -> 0; 2,3 -> 1; 4+ -> 2). */
export function bandIndexForLaunch(launchIndex: number): 0 | 1 | 2 {
  if (!Number.isFinite(launchIndex) || launchIndex < 2) return 0;
  if (launchIndex < 4) return 1;
  return 2;
}

function safeUnit(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  return Math.max(0, Math.min(0.999999999, value));
}

/**
 * Picks a target inside the launch's band. When the band has more than one value and
 * previousTarget lies in it, that value is excluded so consecutive targets differ.
 */
export function pickFuelTarget(
  launchIndex: number,
  previousTarget: number | null,
  random: () => number,
): number {
  const band = FUEL_BANDS[bandIndexForLaunch(launchIndex)];
  const values: number[] = [];
  for (let v = band.min; v <= band.max; v++) {
    if (v !== previousTarget) values.push(v);
  }
  if (values.length === 0) values.push(band.min);
  const index = Math.min(values.length - 1, Math.floor(safeUnit(random()) * values.length));
  return values[index];
}

/** Number of stars shown for a target: min(target + 3, 13), target clamped 1..10. */
export function fuelStarCount(target: number): number {
  const t = Number.isFinite(target)
    ? Math.min(FUEL_MAX, Math.max(FUEL_MIN, Math.round(target)))
    : FUEL_MIN;
  return Math.min(t + 3, FUEL_MAX_STARS);
}

export type FuelPhase = 'idle' | 'collecting' | 'launching' | 'finished';

export type FuelCollectResult =
  | { kind: 'collected'; count: number; complete: boolean; epoch: number }
  | { kind: 'ignored'; reason: 'duplicate' | 'full' | 'not-collecting' | 'invalid' };

/** Pure domain model for a 5-launch "Fuel the Rocket" session. No DOM dependencies. */
export class FuelSession {
  private _epoch = 0;
  private _launchIndex = 0;
  private _target: number;
  private _phase: FuelPhase = 'idle';
  private _collected = new Set<number>();
  private readonly _random: () => number;

  constructor(random: () => number = Math.random) {
    this._random = random;
    this._target = FUEL_BANDS[0].min;
  }

  get epoch(): number {
    return this._epoch;
  }

  get launchIndex(): number {
    return this._launchIndex;
  }

  get target(): number {
    return this._target;
  }

  get phase(): FuelPhase {
    return this._phase;
  }

  get count(): number {
    return this._collected.size;
  }

  get starTotal(): number {
    return fuelStarCount(this._target);
  }

  isCurrent(epoch: number): boolean {
    return epoch === this._epoch;
  }

  isCollected(id: number): boolean {
    return this._collected.has(id);
  }

  collectedIds(): number[] {
    return Array.from(this._collected).sort((a, b) => a - b);
  }

  start(): number {
    this._launchIndex = 0;
    this._collected.clear();
    this._target = pickFuelTarget(0, null, this._random);
    this._phase = 'collecting';
    this._epoch += 1;
    return this._epoch;
  }

  collect(id: number): FuelCollectResult {
    if (this._phase !== 'collecting') {
      return { kind: 'ignored', reason: this._phase === 'launching' ? 'full' : 'not-collecting' };
    }
    if (typeof id !== 'number' || !Number.isInteger(id) || id < 0 || id >= this.starTotal) {
      return { kind: 'ignored', reason: 'invalid' };
    }
    if (this._collected.has(id)) {
      return { kind: 'ignored', reason: 'duplicate' };
    }
    if (this._collected.size >= this._target) {
      return { kind: 'ignored', reason: 'full' };
    }
    this._collected.add(id);
    const count = this._collected.size;
    const complete = count === this._target;
    if (complete) this._phase = 'launching';
    return { kind: 'collected', count, complete, epoch: this._epoch };
  }

  nextRound(epoch: number): 'next' | 'finished' | 'stale' {
    if (!this.isCurrent(epoch) || this._phase !== 'launching') return 'stale';
    this._collected.clear();
    if (this._launchIndex + 1 >= FUEL_LAUNCHES) {
      this._phase = 'finished';
      this._epoch += 1;
      return 'finished';
    }
    this._launchIndex += 1;
    this._target = pickFuelTarget(this._launchIndex, this._target, this._random);
    this._phase = 'collecting';
    this._epoch += 1;
    return 'next';
  }

  invalidate(): void {
    this._epoch += 1;
    this._phase = 'idle';
  }
}
